import {BreakdownType} from '@vio/core/src/api-types/bovio/response/booking'
import {toFloatWithTwoDecimals} from '@vio/core/src/utils/number'

interface PayAtPropertyFeeLine {
  type: string
  total: number
  elements: string[]
}

/** `tax` has no label of its own, it is shown as "Other local fees". */
const asOtherLocalFees = (fee: PayAtPropertyFeeLine): PayAtPropertyFeeLine =>
  fee.type === BreakdownType.Tax ? {...fee, type: BreakdownType.Other} : fee

/**
 * Turns a hotel fees breakdown into the fee lines shown for pay-at-property.
 *
 * `tax` and `other` are both shown as "Other local fees". Only when the
 * breakdown has both are they summed into a single line, so the label is not
 * repeated. Either one on its own stays a line of its own, under that label.
 *
 * @param breakdown - hotel fees breakdown, whose totals can arrive as strings
 * @returns lines with a positive total only, `tax` relabelled as `other`
 */
export const toPayAtPropertyFeeLines = (
  breakdown: {type: string; total: number | string; elements?: string[]}[] = []
): PayAtPropertyFeeLine[] => {
  const fees = breakdown
    .map(fee => ({
      type: fee.type,
      total: toFloatWithTwoDecimals(Number(fee.total)) || 0,
      elements: fee.elements ?? []
    }))
    .filter(fee => fee.total > 0)

  const taxFee = fees.find(fee => fee.type === BreakdownType.Tax)
  const otherFee = fees.find(fee => fee.type === BreakdownType.Other)
  if (!taxFee || !otherFee) return fees.map(asOtherLocalFees)

  const otherLocalFees: PayAtPropertyFeeLine = {
    ...otherFee,
    total: toFloatWithTwoDecimals(otherFee.total + taxFee.total) || 0
  }

  return fees
    .filter(fee => fee !== taxFee)
    .map(fee => (fee === otherFee ? otherLocalFees : fee))
}
